import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { adminApi, positionsApi, type NeedsAttentionItem, type CategoryExposure } from '../utils/api'
import {
  formatProbability,
  formatEdge,
  formatTimeAgo,
  getEdgeClass,
  formatTimeCountdown,
  getTimeCountdownClass,
  getBrierClass,
  getBrierLabel,
} from '../utils/format'
import { DashboardSkeleton } from './Skeleton'
import { Tabs, TabList, Tab, TabPanel } from './Tabs'
import clsx from 'clsx'
import { useState } from 'react'

// Tooltip component
function Tooltip({ children, content }: { children: React.ReactNode; content: string }) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs bg-void-200 border border-star-dim/30 rounded-lg shadow-lg whitespace-nowrap">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-void-200" />
        </div>
      )}
    </div>
  )
}

export function Dashboard() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  type SettlementResponse = Awaited<ReturnType<typeof positionsApi.settle>>
  const [settlementResult, setSettlementResult] = useState<{
    settled_count: number
    total_realized_pnl: number
    winners: number
    losers: number
    win_rate: number
  } | null>(null)
  const [settlePreview, setSettlePreview] = useState<SettlementResponse | null>(null)
  const [settleConfirmOpen, setSettleConfirmOpen] = useState(false)

  // Persist tab selection in URL
  const currentTab = searchParams.get('tab') || 'overview'
  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (tab === 'overview') {
        next.delete('tab')
      } else {
        next.set('tab', tab)
      }
      return next
    }, { replace: true })
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: adminApi.dashboard,
    refetchInterval: 60000, // Refresh every minute
  })

  const settleMutation = useMutation({
    mutationFn: () => positionsApi.settle(false),
    onSuccess: (result) => {
      setSettlementResult(result)
      setSettlePreview(null)
      setSettleConfirmOpen(false)
      // Invalidate dashboard to refresh the unsettled count
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })

  const settlePreviewMutation = useMutation({
    mutationFn: () => positionsApi.settle(true),
    onSuccess: (result) => {
      setSettlePreview(result)
      setSettleConfirmOpen(true)
    },
  })

  const refreshMarketDataMutation = useMutation({
    mutationFn: adminApi.refreshMarketData,
    onSuccess: () => {
      // Invalidate dashboard to refresh the stale data count
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
            Dashboard
          </h2>
          <p className="text-star-muted text-xs sm:text-sm">
            System overview and portfolio health
          </p>
        </div>
        <button
          className="btn-secondary self-start min-h-[44px]"
          onClick={() => refetch()}
          aria-label="Refresh dashboard data"
        >
          <span aria-hidden="true">↻</span> Refresh
        </button>
      </div>

      {/* Loading state */}
      {isLoading && <DashboardSkeleton />}

      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load dashboard</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}

      {data && (
        <>
          {/* Settlement Success Message */}
          {settlementResult && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 bg-signal-green/10 border border-signal-green/30 rounded-lg flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            >
              <span className="text-signal-green text-lg">✓</span>
              <div className="flex-1">
                <span className="text-star text-sm font-medium">
                  Settled {settlementResult.settled_count} position{settlementResult.settled_count !== 1 ? 's' : ''}
                </span>
                <span className={clsx(
                  'text-sm ml-2 font-mono',
                  settlementResult.total_realized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
                )}>
                  {settlementResult.total_realized_pnl >= 0 ? '+' : ''}${settlementResult.total_realized_pnl.toFixed(2)} P&L
                </span>
                <span className="text-star-dim text-xs ml-2">
                  ({settlementResult.winners}W/{settlementResult.losers}L, {settlementResult.win_rate.toFixed(0)}% win rate)
                </span>
              </div>
              <button
                onClick={() => setSettlementResult(null)}
                className="text-xs text-star-dim hover:text-star self-start sm:self-auto"
              >
                Dismiss
              </button>
            </motion.div>
          )}

          {/* Unsettled Resolved Positions Warning */}
          {data.stats.data_freshness && data.stats.data_freshness.unsettled_resolved_count > 0 && !settlementResult && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 bg-signal-purple/10 border border-signal-purple/30 rounded-lg flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            >
              <span className="text-signal-purple text-lg">$</span>
              <div className="flex-1">
                <span className="text-star text-sm font-medium">Positions ready to settle</span>
                <span className="text-star-muted text-sm ml-2">
                  {data.stats.data_freshness.unsettled_resolved_count} resolved position{data.stats.data_freshness.unsettled_resolved_count !== 1 ? 's' : ''} awaiting settlement
                </span>
              </div>
              <div className="flex items-center gap-3 self-start sm:self-auto">
                <button
                  onClick={() => settlePreviewMutation.mutate()}
                  disabled={settlePreviewMutation.isPending || settleMutation.isPending}
                  className={clsx(
                    'px-3 py-1.5 rounded text-xs font-medium transition-colors min-h-[32px]',
                    settlePreviewMutation.isPending || settleMutation.isPending
                      ? 'bg-signal-purple/20 text-signal-purple/50 cursor-wait'
                      : 'bg-signal-purple text-white hover:bg-signal-purple/80'
                  )}
                >
                  {settlePreviewMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Preparing...
                    </span>
                  ) : settleMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Settling...
                    </span>
                  ) : (
                    'Settle All'
                  )}
                </button>
                <Link to="/positions" className="text-xs text-signal-purple hover:underline">
                  View
                </Link>
              </div>
            </motion.div>
          )}

          {/* Settlement Error */}
          {settleMutation.isError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 bg-signal-red/10 border border-signal-red/30 rounded-lg flex items-center gap-3"
            >
              <span className="text-signal-red text-lg">!</span>
              <div className="flex-1">
                <span className="text-star text-sm font-medium">Settlement failed</span>
                <span className="text-star-muted text-sm ml-2">
                  {settleMutation.error instanceof Error ? settleMutation.error.message : 'Unknown error'}
                </span>
              </div>
              <button
                onClick={() => settleMutation.reset()}
                className="text-xs text-star-dim hover:text-star"
              >
                Dismiss
              </button>
            </motion.div>
          )}

          {/* Data Freshness Warning */}
          {data.stats.data_freshness?.has_stale_market_data && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 bg-signal-amber/10 border border-signal-amber/30 rounded-lg flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            >
              <span className="text-signal-amber text-lg">!</span>
              <div className="flex-1">
                <span className="text-star text-sm font-medium">Stale market data</span>
                <span className="text-star-muted text-sm ml-2">
                  {data.stats.data_freshness.stale_position_count} position{data.stats.data_freshness.stale_position_count !== 1 ? 's' : ''} with outdated prices
                  {data.stats.data_freshness.oldest_market_data_mins && (
                    <span className="text-star-dim">
                      {' '}(oldest: {Math.round(data.stats.data_freshness.oldest_market_data_mins)}m ago)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3 self-start sm:self-auto">
                {data.stats.data_freshness.last_run_mins_ago && data.stats.data_freshness.last_run_mins_ago > 60 && (
                  <span className="text-xs text-star-dim">
                    Last run: {Math.round(data.stats.data_freshness.last_run_mins_ago / 60)}h ago
                  </span>
                )}
                <button
                  onClick={() => {
                    if (window.confirm('Refresh market data now? This may take a minute.')) {
                      refreshMarketDataMutation.mutate()
                    }
                  }}
                  disabled={refreshMarketDataMutation.isPending}
                  className={clsx(
                    'px-3 py-1.5 rounded text-xs font-medium transition-colors min-h-[32px]',
                    refreshMarketDataMutation.isPending
                      ? 'bg-signal-amber/20 text-signal-amber/50 cursor-wait'
                      : 'bg-signal-amber text-white hover:bg-signal-amber/80'
                  )}
                >
                  {refreshMarketDataMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Refreshing...
                    </span>
                  ) : (
                    '↻ Refresh Data'
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Dashboard Tabs */}
          <div className="mb-4 sm:mb-6">
            <Tabs defaultTab={currentTab} onChange={handleTabChange}>
              <TabList className="mb-4">
                <Tab value="overview" icon="◉">Overview</Tab>
                <Tab value="risk" icon="⚠">Risk</Tab>
                <Tab value="activity" icon="▶">Activity</Tab>
              </TabList>

              <TabPanel value="overview">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="glass-card p-5"
                  >
                    <div className="data-label">Total Questions</div>
                    <div className="text-3xl font-mono font-semibold text-signal-blue mt-1">
                      {data.stats.total_questions.toLocaleString()}
                    </div>
                    <div className="text-xs text-star-muted mt-1">
                      {data.stats.open_questions} open
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="glass-card p-5"
                  >
                    <div className="data-label">Prediction Runs</div>
                    <div className="text-3xl font-mono font-semibold text-signal-purple mt-1">
                      {data.stats.total_runs.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-star-muted mt-1">
                      <span>{data.stats.runs_24h} today</span>
                      {data.stats.runs_7d > 0 && (
                        <span className="text-star-dim">
                          ({Math.round(data.stats.runs_7d / 7)}/day avg)
                        </span>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="glass-card p-5"
                  >
                    <Tooltip content="Brier Score: Lower is better. <0.10 = Excellent, <0.15 = Good, <0.25 = Fair, >0.25 = Poor">
                      <div className="data-label cursor-help flex items-center gap-1">
                        Avg Brier Score
                        <span className="text-star-dim">ⓘ</span>
                      </div>
                    </Tooltip>
                    <div className={clsx(
                      'text-3xl font-mono font-semibold mt-1',
                      getBrierClass(data.stats.avg_brier)
                    )}>
                      {data.stats.avg_brier !== null ? data.stats.avg_brier.toFixed(3) : '—'}
                    </div>
                    <div className="flex items-center gap-2 text-xs mt-1">
                      <span className={getBrierClass(data.stats.avg_brier)}>
                        {getBrierLabel(data.stats.avg_brier)}
                      </span>
                      {data.stats.avg_brier_7d !== null && data.stats.avg_brier !== null && (
                        <span className={clsx(
                          'text-xs',
                          data.stats.avg_brier_7d < data.stats.avg_brier ? 'text-signal-green' : 'text-signal-red'
                        )}>
                          {data.stats.avg_brier_7d < data.stats.avg_brier ? '↓ improving' : '↑ worsening'}
                        </span>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="glass-card p-5"
                  >
                    <div className="data-label">Portfolio P&L</div>
                    <div className={clsx(
                      'text-3xl font-mono font-semibold mt-1',
                      data.stats.total_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
                    )}>
                      {data.stats.total_pnl >= 0 ? '+' : ''}${data.stats.total_pnl.toFixed(2)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-star-muted mt-1">
                      <span>{data.stats.total_positions} positions</span>
                      <span className="text-star-dim">•</span>
                      <span>${data.stats.total_exposure.toFixed(0)} exposure</span>
                    </div>
                    {data.stats.unrealized_pnl !== 0 && (
                      <div className={clsx(
                        'text-xs mt-0.5',
                        data.stats.unrealized_pnl >= 0 ? 'text-signal-green/70' : 'text-signal-red/70'
                      )}>
                        ({data.stats.unrealized_pnl >= 0 ? '+' : ''}${data.stats.unrealized_pnl.toFixed(2)} unrealized)
                      </div>
                    )}
                  </motion.div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Link
                    to="/questions?filter=high_edge"
                    className="glass-card p-4 hover:bg-void-50/50 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl" aria-hidden="true">🎯</span>
                    <div>
                      <div className="font-medium text-star text-sm">High Edge</div>
                      <div className="text-xs text-star-muted">Questions with &gt;5% edge</div>
                    </div>
                  </Link>
                  <Link
                    to="/questions?filter=closing_soon"
                    className="glass-card p-4 hover:bg-void-50/50 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl" aria-hidden="true">⏰</span>
                    <div>
                      <div className="font-medium text-star text-sm">Closing Soon</div>
                      <div className="text-xs text-star-muted">Markets closing in &lt;24h</div>
                    </div>
                  </Link>
                  <Link
                    to="/forecast"
                    className="glass-card p-4 hover:bg-void-50/50 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl" aria-hidden="true">🔮</span>
                    <div>
                      <div className="font-medium text-star text-sm">New Forecast</div>
                      <div className="text-xs text-star-muted">Make a prediction</div>
                    </div>
                  </Link>
                </div>
              </TabPanel>

              <TabPanel value="risk">
                {/* Needs Attention & Risk Summary Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Needs Attention */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="glass-card overflow-hidden"
                  >
                    <div className="px-6 py-4 border-b border-star-dim/20 flex items-center justify-between">
                      <h3 className="font-display font-semibold text-star flex items-center gap-2">
                        <span className="text-signal-amber" aria-hidden="true">⚠</span>
                        Needs Attention
                      </h3>
                      <span className="badge-muted text-xs">{data.needs_attention.length} items</span>
                    </div>

                    {data.needs_attention.length > 0 ? (
                      <div className="divide-y divide-star-dim/10 max-h-80 overflow-y-auto">
                        {data.needs_attention.map((item, idx) => (
                          <NeedsAttentionRow key={item.question_id} item={item} index={idx} />
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <span className="text-signal-green text-2xl" aria-hidden="true">✓</span>
                        <p className="text-star-muted font-mono text-sm mt-2">All positions healthy</p>
                      </div>
                    )}
                  </motion.div>

                  {/* Risk Summary */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="glass-card overflow-hidden"
                  >
                    <div className="px-6 py-4 border-b border-star-dim/20">
                      <h3 className="font-display font-semibold text-star flex items-center gap-2">
                        <span className="text-signal-purple" aria-hidden="true">◈</span>
                        Portfolio Risk
                      </h3>
                    </div>

                    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                      {/* Risk Metrics */}
                      <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="p-3 bg-void-50 rounded-lg">
                          <div className="text-xs text-star-dim mb-1">Largest Position</div>
                          <div className="text-sm font-mono text-star truncate" title={data.risk_summary.largest_position_title || undefined}>
                            ${data.risk_summary.largest_position_exposure.toFixed(0)}
                          </div>
                          <div className="text-xs text-star-muted truncate">
                            {data.risk_summary.largest_position_title || '—'}
                          </div>
                        </div>

                        <div className="p-3 bg-void-50 rounded-lg">
                          <div className="text-xs text-star-dim mb-1">Concentration</div>
                          <div className={clsx(
                            'text-sm font-mono',
                            data.risk_summary.concentration_warning ? 'text-signal-amber' : 'text-signal-green'
                          )}>
                            {data.risk_summary.concentration_warning || 'Well diversified'}
                          </div>
                        </div>

                        <div className="p-3 bg-void-50 rounded-lg">
                          <div className="text-xs text-star-dim mb-1">Closing Soon</div>
                          <div className={clsx(
                            'text-sm font-mono',
                            data.risk_summary.positions_closing_soon > 2 ? 'text-signal-amber' : 'text-star'
                          )}>
                            {data.risk_summary.positions_closing_soon} positions
                          </div>
                          <div className="text-xs text-star-muted">within 24 hours</div>
                        </div>

                        <div className="p-3 bg-void-50 rounded-lg">
                          <div className="text-xs text-star-dim mb-1">Negative Edge</div>
                          <div className={clsx(
                            'text-sm font-mono',
                            data.risk_summary.positions_with_negative_edge > 0 ? 'text-signal-red' : 'text-signal-green'
                          )}>
                            {data.risk_summary.positions_with_negative_edge} positions
                          </div>
                          <div className="text-xs text-star-muted">below entry edge</div>
                        </div>
                      </div>

                      {/* Category Breakdown */}
                      {data.risk_summary.category_breakdown.length > 0 && (
                        <div>
                          <div className="text-xs text-star-dim mb-2">Exposure by Category</div>
                          <div className="space-y-1.5">
                            {data.risk_summary.category_breakdown.slice(0, 6).map((cat) => (
                              <CategoryExposureBar key={cat.category} category={cat} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              </TabPanel>

              <TabPanel value="activity">
                {/* Recent Predictions */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="glass-card overflow-hidden"
                >
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-star-dim/20 flex items-center justify-between">
                    <h3 className="font-display font-semibold text-star text-sm sm:text-base">
                      Recent Predictions
                    </h3>
                    <Link to="/runs" className="text-xs text-signal-blue hover:underline">
                      View all runs →
                    </Link>
                  </div>

                  {data.recent_predictions.length > 0 ? (
                    <>
                      {/* Mobile Card View */}
                      <div className="md:hidden divide-y divide-star-dim/10">
                        {data.recent_predictions.map((card, index) => {
                          const countdown = formatTimeCountdown(card.close_ts)
                          const getTradeSignal = () => {
                            if (card.edge === null || card.edge === undefined) {
                              return { text: '—', color: 'text-star-dim', icon: '' }
                            }
                            if (card.edge > 0.02) {
                              return { text: 'BUY YES', color: 'text-signal-green', icon: '↑' }
                            }
                            if (card.edge < -0.02) {
                              return { text: 'OVERPRICED', color: 'text-signal-amber', icon: '↓' }
                            }
                            return { text: 'NEUTRAL', color: 'text-star-muted', icon: '→' }
                          }
                          const signal = getTradeSignal()
                          return (
                            <motion.div
                              key={card.question_id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.15 + index * 0.03 }}
                            >
                              <Link
                                to={`/question/${card.question_id}`}
                                className="block p-4 hover:bg-void-50/50 active:bg-void-100/50 transition-colors"
                              >
                                {/* Title and Signal */}
                                <div className="flex items-start justify-between gap-3 mb-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-star line-clamp-2">
                                      {card.title}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      {card.category && (
                                        <span className="text-xs text-star-muted">{card.category}</span>
                                      )}
                                      {card.analyzed_outcome_name && (
                                        <span className="text-xs px-1.5 py-0.5 bg-signal-blue/10 text-signal-blue rounded">
                                          {card.analyzed_outcome_name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className={clsx(
                                    'text-[10px] font-mono font-semibold px-2 py-1 rounded whitespace-nowrap shrink-0',
                                    signal.color,
                                    signal.text === 'BUY YES' && 'bg-signal-green/10',
                                    signal.text === 'OVERPRICED' && 'bg-signal-amber/10',
                                    signal.text === 'NEUTRAL' && 'bg-star-dim/10'
                                  )}>
                                    {signal.icon} {signal.text}
                                  </span>
                                </div>

                                {/* Stats Row */}
                                <div className="grid grid-cols-4 gap-2 text-center">
                                  <div>
                                    <div className="text-[10px] text-star-dim mb-0.5">Ours</div>
                                    <div className="font-mono text-xs text-star">{formatProbability(card.our_prob)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] text-star-dim mb-0.5">Market</div>
                                    <div className="font-mono text-xs text-star-muted">{formatProbability(card.market_prob)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] text-star-dim mb-0.5">Edge</div>
                                    <div className={clsx('font-mono text-xs font-semibold', getEdgeClass(card.edge))}>
                                      {formatEdge(card.edge)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] text-star-dim mb-0.5">Closes</div>
                                    <div className={clsx('font-mono text-xs', getTimeCountdownClass(countdown.urgency))}>
                                      {countdown.text}
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            </motion.div>
                          )
                        })}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-star-dim/10">
                              <th className="text-left p-4 text-xs font-mono text-star-muted font-normal">Question</th>
                              <th className="text-center p-4 text-xs font-mono text-star-muted font-normal">Signal</th>
                              <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Our Prob</th>
                              <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Market</th>
                              <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Edge</th>
                              <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Closes</th>
                              <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Last Run</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.recent_predictions.map((card, index) => {
                              const countdown = formatTimeCountdown(card.close_ts)
                              const getTradeSignal = () => {
                                if (card.edge === null || card.edge === undefined) {
                                  return { text: '—', color: 'text-star-dim', icon: '' }
                                }
                                if (card.edge > 0.02) {
                                  return { text: 'BUY YES', color: 'text-signal-green', icon: '↑' }
                                }
                                if (card.edge < -0.02) {
                                  return { text: 'OVERPRICED', color: 'text-signal-amber', icon: '↓' }
                                }
                                return { text: 'NEUTRAL', color: 'text-star-muted', icon: '→' }
                              }
                              const signal = getTradeSignal()
                              return (
                                <motion.tr
                                  key={card.question_id}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.15 + index * 0.03 }}
                                  className="border-b border-star-dim/5 hover:bg-void-50/50"
                                >
                                  <td className="p-4">
                                    <Link
                                      to={`/question/${card.question_id}`}
                                      className="text-star hover:text-signal-blue transition-colors"
                                    >
                                      <div className="font-medium text-sm line-clamp-2">
                                        {card.title}
                                      </div>
                                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {card.category && (
                                          <span className="text-xs text-star-muted">
                                            {card.category}
                                          </span>
                                        )}
                                        {card.analyzed_outcome_name && (
                                          <span className="text-xs px-1.5 py-0.5 bg-signal-blue/10 text-signal-blue rounded">
                                            {card.analyzed_outcome_name}
                                          </span>
                                        )}
                                      </div>
                                    </Link>
                                  </td>
                                  <td className="p-4 text-center">
                                    <span className={clsx(
                                      'text-xs font-mono font-semibold px-3 py-1 rounded whitespace-nowrap',
                                      signal.color,
                                      signal.text === 'BUY YES' && 'bg-signal-green/10',
                                      signal.text === 'OVERPRICED' && 'bg-signal-amber/10',
                                      signal.text === 'NEUTRAL' && 'bg-star-dim/10'
                                    )}>
                                      {signal.icon} {signal.text}
                                    </span>
                                  </td>
                                  <td className="p-4 text-right font-mono text-sm text-star">
                                    {formatProbability(card.our_prob)}
                                  </td>
                                  <td className="p-4 text-right font-mono text-sm text-star-muted">
                                    {formatProbability(card.market_prob)}
                                  </td>
                                  <td className="p-4 text-right">
                                    <span className={clsx('font-mono text-sm font-semibold', getEdgeClass(card.edge))}>
                                      {formatEdge(card.edge)}
                                    </span>
                                  </td>
                                  <td className={clsx('p-4 text-right font-mono text-sm', getTimeCountdownClass(countdown.urgency))}>
                                    {countdown.text}
                                  </td>
                                  <td className="p-4 text-right text-xs text-star-dim">
                                    {formatTimeAgo(card.last_run_at)}
                                  </td>
                                </motion.tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="p-8 text-center">
                      <span className="text-3xl text-star-dim" aria-hidden="true">◎</span>
                      <p className="text-star-muted font-mono text-sm mt-2">No recent predictions</p>
                      <Link to="/questions" className="text-xs text-signal-blue hover:underline mt-2 inline-block">
                        Browse questions to make predictions
                      </Link>
                    </div>
                  )}
                </motion.div>
              </TabPanel>
            </Tabs>
          </div>

          {/* Settlement Confirmation Modal */}
          {settleConfirmOpen && settlePreview && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[250] bg-void/80 backdrop-blur-sm p-4 flex items-center justify-center"
                onClick={() => !settleMutation.isPending && setSettleConfirmOpen(false)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.98, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: 10 }}
                  className="glass-card w-full max-w-lg p-5 sm:p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-display font-semibold text-star text-base">Confirm Settlement</h3>
                      <p className="text-xs text-star-muted mt-1">
                        This will mark resolved positions as settled and update realized P&amp;L.
                      </p>
                    </div>
                    <button
                      className="p-2 -mr-2 -mt-2 rounded-lg text-star-dim hover:text-star hover:bg-void-100 transition-colors"
                      onClick={() => setSettleConfirmOpen(false)}
                      disabled={settleMutation.isPending}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 bg-void-50 rounded-lg">
                      <div className="text-xs text-star-dim mb-1">Positions</div>
                      <div className="text-sm font-mono text-star">{settlePreview.settled_count}</div>
                    </div>
                    <div className="p-3 bg-void-50 rounded-lg">
                      <div className="text-xs text-star-dim mb-1">Cost Basis</div>
                      <div className="text-sm font-mono text-star">${settlePreview.total_cost_basis.toFixed(2)}</div>
                    </div>
                    <div className="p-3 bg-void-50 rounded-lg">
                      <div className="text-xs text-star-dim mb-1">Realized P&amp;L</div>
                      <div
                        className={clsx(
                          'text-sm font-mono',
                          settlePreview.total_realized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
                        )}
                      >
                        {settlePreview.total_realized_pnl >= 0 ? '+' : ''}${settlePreview.total_realized_pnl.toFixed(2)}
                      </div>
                    </div>
                    <div className="p-3 bg-void-50 rounded-lg">
                      <div className="text-xs text-star-dim mb-1">ROI</div>
                      <div className="text-sm font-mono text-star">{settlePreview.total_roi_percent.toFixed(1)}%</div>
                    </div>
                  </div>

                  {settlePreview.results.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs text-star-dim mb-2">Preview (first 8)</div>
                      <div className="max-h-48 overflow-y-auto rounded-lg border border-star-dim/20">
                        <div className="divide-y divide-star-dim/10">
                          {settlePreview.results.slice(0, 8).map((r) => (
                            <div key={r.question_id} className="p-3 flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-xs text-star line-clamp-2">{r.question_title}</div>
                                <div className="text-[10px] text-star-dim font-mono mt-1">
                                  {r.outcome} • {r.yes_qty.toFixed(2)} yes @ ${r.avg_cost.toFixed(3)}
                                </div>
                              </div>
                              <div
                                className={clsx(
                                  'text-xs font-mono shrink-0',
                                  r.realized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
                                )}
                              >
                                {r.realized_pnl >= 0 ? '+' : ''}${r.realized_pnl.toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="btn-secondary"
                      onClick={() => setSettleConfirmOpen(false)}
                      disabled={settleMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={() => settleMutation.mutate()}
                      disabled={settleMutation.isPending}
                    >
                      {settleMutation.isPending ? 'Settling…' : 'Confirm Settle'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}
        </>
      )}
    </div>
  )
}

// Subcomponent for needs attention items
function NeedsAttentionRow({ item, index }: { item: NeedsAttentionItem; index: number }) {
  const urgencyColors = {
    critical: 'border-l-signal-red bg-signal-red/5',
    warning: 'border-l-signal-amber bg-signal-amber/5',
    info: 'border-l-signal-blue bg-signal-blue/5',
  }

  const urgencyIcons = {
    critical: '🔴',
    warning: '🟡',
    info: '🔵',
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.35 + index * 0.05 }}
    >
      <Link
        to={`/question/${item.question_id}`}
        className={clsx(
          'block p-4 border-l-4 hover:bg-void-50/50 transition-colors',
          urgencyColors[item.urgency]
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs">{urgencyIcons[item.urgency]}</span>
              <span className="text-xs font-mono text-star-muted">{item.reason}</span>
            </div>
            <div className="text-sm text-star truncate">{item.title}</div>
          </div>
          {item.edge !== null && (
            <div className={clsx(
              'text-xs font-mono font-semibold shrink-0',
              getEdgeClass(item.edge)
            )}>
              {formatEdge(item.edge)}
            </div>
          )}
        </div>
        {item.hours_to_close !== null && (
          <div className="text-xs text-star-dim mt-1">
            ⏱ {item.hours_to_close.toFixed(0)}h remaining
          </div>
        )}
      </Link>
    </motion.div>
  )
}

// Subcomponent for category exposure bars
function CategoryExposureBar({ category }: { category: CategoryExposure }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 sm:w-24 text-xs text-star-muted truncate" title={category.category}>
        {category.category}
      </div>
      <div className="flex-1 h-2 bg-void-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all',
            category.pct_of_total > 40 ? 'bg-signal-amber' : 'bg-signal-blue'
          )}
          style={{ width: `${Math.min(100, category.pct_of_total)}%` }}
        />
      </div>
      <div className="w-12 text-xs text-right font-mono text-star-muted">
        {category.pct_of_total.toFixed(0)}%
      </div>
      <div className={clsx(
        'w-14 text-xs text-right font-mono',
        category.avg_edge !== null && category.avg_edge > 0 ? 'text-signal-green' :
        category.avg_edge !== null && category.avg_edge < 0 ? 'text-signal-red' : 'text-star-dim'
      )}>
        {category.avg_edge !== null ? `${(category.avg_edge * 100).toFixed(1)}%` : '—'}
      </div>
    </div>
  )
}
